import React from 'react';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useBlocker } from 'react-router-dom';

import { UxContext } from '@client/context/UxContext';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Grid, Stack, useMediaQuery, useTheme } from '@mui/material';

interface LediumFormProps {
    header?: React.ReactNode;
    children?: React.ReactNode;
    formRef?: React.RefObject<unknown>;
    onSubmit: () => void;
    onCancel?: () => void;
    saveDisabled?: boolean;
    showButtons?: boolean;
    saveText?: React.ReactNode;
    cancelText?: React.ReactNode;
    additionalButton?: React.ReactNode;
    isDirtyOverride?: boolean;
}

export const LediumForm = ({
    header,
    children,
    formRef,
    onSubmit,
    onCancel,
    saveDisabled = false,
    showButtons = true,
    saveText = i18n('save'),
    cancelText = i18n('cancel'),
    additionalButton,
    isDirtyOverride
}: LediumFormProps) => {
    const {
        pageState: { formData, formBaselineHash, isFormSubmitting }
    } = usePageStore();

    const { handleAlert } = React.useContext(UxContext)!;
    const isDirty = isDirtyOverride === undefined ? formBaselineHash !== null && JSON.stringify(formData) !== formBaselineHash : isDirtyOverride;
    const blocker = useBlocker(isDirty && !isFormSubmitting);
    const buttons: { key: string; node: React.ReactNode }[] = [];

    React.useEffect(() => {
        if (blocker.state === 'blocked') {
            handleAlert({
                title: i18n('unsaved_changes_title') as string,
                message: i18n('unsaved_changes_message') as string,
                okText: i18n('leave_page'),
                cancelText: i18n('stay_on_page'),
                okCallback: () => blocker.proceed(),
                cancelCallback: () => blocker.reset()
            });
        }
    }, [blocker, handleAlert]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (additionalButton) {
        buttons.push({ key: 'additional', node: additionalButton });
    }
    if (onCancel) {
        buttons.push({
            key: 'cancel',
            node: (
                <Button onClick={onCancel} startIcon={<CancelIcon />}>
                    {cancelText}
                </Button>
            )
        });
    }

    if (!formData.readOnly) {
        buttons.push({
            key: 'save',
            node: (
                <Button
                    variant="contained"
                    color={onCancel ? 'primary' : 'inherit'}
                    type="submit"
                    startIcon={onCancel ? <SaveIcon /> : <CheckIcon />}
                    disabled={saveDisabled || !isDirty || isFormSubmitting}
                >
                    {saveText}
                </Button>
            )
        });
    }

    return (
        <ValidatorForm onSubmit={onSubmit} ref={formRef ? (formRef as React.RefObject<ValidatorForm>) : null}>
            <Grid container spacing={3}>
                {header ? header : null}
                {children}
                {showButtons && (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                border: '2px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                borderRadius: '8px',
                                padding: '10px',
                                zIndex: 1000
                            }}
                        >
                            <Stack sx={{ width: '100%' }} direction={isMobile ? 'column' : 'row'} justifyContent="space-around" alignItems="center" spacing={4}>
                                {buttons.map(({ key, node }) => (
                                    <React.Fragment key={key}>{node}</React.Fragment>
                                ))}
                            </Stack>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </ValidatorForm>
    );
};
